import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Save,
  Package,
  AlertCircle,
  ShieldOff,
  MapPin,
  FolderOpen,
  Users,
  Calendar,
} from "lucide-react";
import { itemTypesApi, type CreateItemTypeDto } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { Collection } from "@/lib/api/collections";
import type { PatronType } from "@/lib/api/patron-types";
import { useLocations } from "@/hooks/useLocations";
import { useCollections } from "@/hooks/useCollections";
import { usePatronTypes } from "@/hooks/usePatronTypes";
import { ITEM_TYPES_QUERY_KEY } from "@/hooks/useItemTypes";
import { cn } from "@/lib/utils";

const ItemTypeFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: locationsRaw = [], isLoading: loadingLocs } = useLocations(true);
  const { data: collectionsRaw = [], isLoading: loadingCols } = useCollections(true);
  const { data: patronTypesRaw = [], isLoading: loadingPatrons } = usePatronTypes(true);

  const locations = locationsRaw.filter((l) => l.isActive);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const patronTypes = patronTypesRaw.filter((p) => p.isActive);
  const loadingRefs = loadingLocs || loadingCols || loadingPatrons;

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateItemTypeDto>({
    name: "",
    code: "",
    description: "",
    isBorrowable: true,
    loanPeriodOverride: undefined,
    fineAmountOverride: undefined,
    maxRenewalsOverride: undefined,
    defaultLocationId: undefined,
    defaultCollectionId: undefined,
    allowedPatronTypeIds: [],
  });

  const { isTenantAdmin } = useAccessControl();
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadItemType(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  useEffect(() => {
    if (!loading && !loadingRefs) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  const loadItemType = async (itemTypeId: string) => {
    try {
      setLoading(true);
      const itemType = await itemTypesApi.getById(itemTypeId);
      setFormData({
        name: itemType.name,
        code: itemType.code,
        description: itemType.description || "",
        isBorrowable: itemType.isBorrowable,
        loanPeriodOverride: itemType.loanPeriodOverride || undefined,
        fineAmountOverride: itemType.fineAmountOverride || undefined,
        maxRenewalsOverride: itemType.maxRenewalsOverride || undefined,
        defaultLocationId: itemType.defaultLocationId || undefined,
        defaultCollectionId: itemType.defaultCollectionId || undefined,
        allowedPatronTypeIds: itemType.allowedPatronTypes?.map((apt) => apt.patronType.id) || [],
      });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load item type.");
      navigate("/setup/item-types");
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "name":
        if (!value || !value.trim()) return "Name is required";
        if (value.length < 2) return "Name must be at least 2 characters";
        return "";
      case "code":
        if (!value || !value.trim()) return "Code is required";
        if (!/^[A-Z0-9_]+$/.test(value))
          return "Use only uppercase letters, numbers, and underscores";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    newErrors.name = validateField("name", formData.name);
    newErrors.code = validateField("code", formData.code);

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );

    setErrors(filteredErrors);
    setTouched(new Set(["name", "code"]));

    return Object.keys(filteredErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    const value = formData[field as keyof typeof formData];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      const submitData: CreateItemTypeDto = {
        ...formData,
        code: formData.code.toUpperCase(),
        description: formData.description || undefined,
        loanPeriodOverride: formData.loanPeriodOverride || undefined,
        fineAmountOverride: formData.fineAmountOverride || undefined,
        maxRenewalsOverride: formData.maxRenewalsOverride || undefined,
        defaultLocationId: formData.defaultLocationId || undefined,
        defaultCollectionId: formData.defaultCollectionId || undefined,
        allowedPatronTypeIds:
          formData.allowedPatronTypeIds && formData.allowedPatronTypeIds.length > 0
            ? formData.allowedPatronTypeIds
            : undefined,
      };

      if (isEditing && id) {
        await itemTypesApi.update(id, submitData);
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        await itemTypesApi.create(submitData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }

      queryClient.invalidateQueries(ITEM_TYPES_QUERY_KEY);
      setHasUnsavedChanges(false);
      navigate("/setup/item-types");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation("/setup/item-types");
      setShowDiscardDialog(true);
    } else {
      navigate("/setup/item-types");
    }
  };

  const handleDiscardConfirm = () => {
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const togglePatronType = (patronTypeId: string) => {
    setFormData((prev) => {
      const currentIds = prev.allowedPatronTypeIds || [];
      const newIds = currentIds.includes(patronTypeId)
        ? currentIds.filter((id) => id !== patronTypeId)
        : [...currentIds, patronTypeId];
      return { ...prev, allowedPatronTypeIds: newIds };
    });
  };

  const getFieldStatus = (field: string) => {
    if (!touched.has(field)) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage item types.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if ((loading && isEditing) || loadingRefs) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading item type..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <div className="mb-6">
          <PageHeader
            title={isEditing ? "Edit Item Type" : "Add Item Type"}
            description={
              isEditing
                ? "Update the settings for this item type"
                : "Create a new category for library materials"
            }
            breadcrumbItems={[
              { label: "Setup", href: "/setup" },
              { label: "Item Types", href: "/setup/item-types" },
              { label: isEditing ? "Edit" : "New" },
            ]}
            showSearch={false}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                <p className="text-sm text-gray-500">Name and identify this item type</p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                    Item Type Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() => handleFieldBlur("name")}
                    placeholder="e.g., Book, Magazine, DVD"
                    className={cn(
                      getFieldStatus("name") === "error" && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {touched.has("name") && errors.name && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="code" className="flex items-center gap-1 mb-2">
                    Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    onBlur={() => handleFieldBlur("code")}
                    placeholder="e.g., BOOK, MAG, DVD"
                    className={cn(
                      getFieldStatus("code") === "error" && "border-red-500 focus:border-red-500"
                    )}
                  />
                  {touched.has("code") && errors.code && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.code}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    Uppercase letters, numbers, and underscores only
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="flex items-center gap-1 mb-2">
                  Description
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Briefly describe this type of material"
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <Checkbox
                  id="isBorrowable"
                  checked={formData.isBorrowable}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isBorrowable: checked === true })
                  }
                />
                <div className="flex-1">
                  <Label htmlFor="isBorrowable" className="font-medium cursor-pointer">
                    Can be borrowed
                  </Label>
                  <p className="text-xs text-gray-500">
                    Uncheck for reference-only materials that stay in the library
                  </p>
                </div>
                <span
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium",
                    formData.isBorrowable
                      ? "bg-primary/10 text-primary"
                      : "bg-gray-200 text-gray-600"
                  )}
                >
                  {formData.isBorrowable ? "Borrowable" : "Not Borrowable"}
                </span>
              </div>
            </div>
          </Card>

          {/* Default Location & Collection */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-teal-100">
                <MapPin className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Default Settings</h3>
                <p className="text-sm text-gray-500">
                  Pre-fill location and collection when adding new items of this type
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="defaultLocationId" className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Default Location
                </Label>
                <Select
                  value={formData.defaultLocationId || "__none__"}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      defaultLocationId: value === "__none__" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="defaultLocationId">
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="defaultCollectionId" className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-4 h-4 text-gray-400" />
                  Default Collection
                </Label>
                <Select
                  value={formData.defaultCollectionId || "__none__"}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      defaultCollectionId: value === "__none__" ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger id="defaultCollectionId">
                    <SelectValue placeholder="Select a collection (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {collections.map((collection) => (
                      <SelectItem key={collection.id} value={collection.id}>
                        {collection.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Borrowing Rules Override */}
          {formData.isBorrowable && (
            <Card className="p-6 mb-6 bg-white shadow-sm">
              <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Borrowing Rules</h3>
                  <p className="text-sm text-gray-500">
                    Override the default rules from patron types (leave blank to use defaults)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <div>
                  <Label htmlFor="loanPeriodOverride" className="mb-2">
                    Loan Period (days)
                  </Label>
                  <Input
                    id="loanPeriodOverride"
                    type="number"
                    min="1"
                    max="365"
                    value={formData.loanPeriodOverride || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        loanPeriodOverride: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Use patron default"
                  />
                </div>
                <div>
                  <Label htmlFor="fineAmountOverride" className="mb-2">
                    Late Fee Per Day
                  </Label>
                  <Input
                    id="fineAmountOverride"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.fineAmountOverride || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fineAmountOverride: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    placeholder="Use patron default"
                  />
                </div>
                <div>
                  <Label htmlFor="maxRenewalsOverride" className="mb-2">
                    Max Renewals
                  </Label>
                  <Input
                    id="maxRenewalsOverride"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.maxRenewalsOverride || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxRenewalsOverride: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Use patron default"
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Patron Type Restrictions */}
          {formData.isBorrowable && (
            <Card className="p-6 mb-6 bg-white shadow-sm">
              <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Who Can Borrow</h3>
                  <p className="text-sm text-gray-500">
                    Restrict which patron types can borrow this material (leave all unchecked for no
                    restrictions)
                  </p>
                </div>
              </div>

              {patronTypes.length === 0 ? (
                <p className="text-sm text-gray-500">No active patron types available</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {patronTypes.map((patronType) => (
                    <div
                      key={patronType.id}
                      className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                    >
                      <Checkbox
                        id={`patron-${patronType.id}`}
                        checked={formData.allowedPatronTypeIds?.includes(patronType.id) || false}
                        onCheckedChange={() => togglePatronType(patronType.id)}
                      />
                      <Label
                        htmlFor={`patron-${patronType.id}`}
                        className="font-normal cursor-pointer"
                      >
                        {patronType.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}

              {formData.allowedPatronTypeIds && formData.allowedPatronTypeIds.length > 0 && (
                <div className="p-3 mt-4 border border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Only <strong>{formData.allowedPatronTypeIds.length}</strong> patron type
                    {formData.allowedPatronTypeIds.length !== 1 ? "s" : ""} can borrow this item
                    type.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Item Type"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      {/* Discard Changes Dialog */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard unsaved changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscardConfirm}
        variant="warning"
      />
    </AppLayout>
  );
};

export default ItemTypeFormPage;
